import { zeroAddress } from 'viem'
import { FluidVaultResolverAbi } from '@1delta/abis'
import { Lender } from '@1delta/lender-registry'
import { fluidResolvers } from '@1delta/data-sdk'
import { formatMorphoPrice } from '../../../utils/parsing'
import { createMarketUid } from '../../../utils'
import type {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'

// Fluid on-chain native-ETH sentinel; normalize to zeroAddress to match the
// lending stack's convention.
const FLUID_EEE_LOWER = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
const normalizeUnderlying = (addr: string): string =>
  addr === FLUID_EEE_LOWER ? zeroAddress : addr

/**
 * Fluid oracle price scale.
 *
 * `oraclePriceOperate` is always "debt per col": amount of debt token for
 * one collateral token, encoded in raw integer units, scaled by 1e27.
 *
 *   raw = debt_human × 10^debtDec × 1e27 / 10^colDec
 *
 * So the human (float) price of 1 col in debt = raw / 10^(27 + debtDec - colDec).
 *
 * Morpho's `formatMorphoPrice` uses `36 + colDec - debtDec`. Fluid uses the
 * same shape but with 27 instead of 36 (Fluid exp = 1e27, Morpho = 1e36) and
 * with `debtDec - colDec` instead of `colDec - debtDec` — because Fluid
 * encodes debt-per-col while Morpho's oracle encodes col-per-debt.
 *
 * To reuse `formatMorphoPrice` we pass (colDec_effective, debtDec_effective)
 * such that `36 + colDec_eff - debtDec_eff == 27 + debtDec - colDec`, i.e.
 *   colDec_eff = debtDec
 *   debtDec_eff = colDec + 9      (since 36 - 27 = 9)
 */
function priceColInDebt(
  raw: string,
  colDec: number,
  debtDec: number,
): number {
  return formatMorphoPrice(raw, debtDec, colDec + 9)
}

/**
 * Meta shape — one entry per non-skipped vault returned by the resolver.
 *
 * All meta for every vault on the chain is packed into a single
 * `FetcherResult` because the whole list comes back from one multicall
 * (`getVaultsEntireData()` is a chain-wide dump).
 */
export interface FluidQueryMeta {
  vaults: Array<{
    vaultId: number
    lender: string
    collateral: string
    loan: string
    colDec: number
    loanDec: number
    rawPrice: string
  }>
}

/**
 * Build multicall calls for Fluid oracle prices.
 *
 * Single call: `VaultResolver.getVaultsEntireData()` returns every vault
 * plus its embedded oracle price (`configs.oraclePriceOperate`). No need
 * to hit per-vault oracle contracts — the resolver already did it.
 *
 * Meta is built lazily in `parse` from the resolver response (we don't
 * have vault data at call-building time, and pre-fetching metadata just
 * to construct meta would double the RPC load).
 */
export function getFluidCalls(
  chainId: string,
): FetcherResult<FluidQueryMeta>[] {
  const resolvers = fluidResolvers()?.[chainId]
  if (!resolvers) return []

  const call: MulticallCall = {
    address: resolvers.vaultResolver,
    name: 'getVaultsEntireData',
    params: [],
  }

  return [
    {
      calls: [call],
      // Empty placeholder — populated per-vault inside `parse` from the
      // resolver response. Kept as an empty object to match the shape
      // other fetchers expose.
      meta: { vaults: [] },
      lender: Lender.FLUID,
    },
  ]
}

/**
 * Parse Fluid oracle results into per-vault price entries.
 *
 * Emits two entries per non-smart vault:
 *   - loan asset  — `staticBase: true`, priceUSD pulled from `context.usdPrices`
 *   - col  asset  — priceUSD = `priceColInDebt × loanUSD`
 *
 * Smart vaults (T2/T3/T4) are deliberately skipped, and this is NOT a gap to
 * be filled later. The point of this fetcher is to derive a USD price for an
 * asset that has no market price of its own, using the vault oracle's
 * collateral-in-debt ratio. On a smart vault that ratio prices **DEX LP shares
 * against LP shares**, not one leg token against another, so no per-leg price
 * can be recovered from it. The legs themselves are ordinary ERC-20s (wstETH,
 * ETH, USDC, …) that are already priced by the normal sources, so nothing is
 * lost — whereas treating a share ratio as a token ratio would be wrong.
 */
export function parseFluidResults(
  data: any[],
  _meta: FluidQueryMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, usdPrices, tokenList } = context
  const entries: OraclePriceEntry[] = []

  const vaults = data[0]
  if (!Array.isArray(vaults) || vaults.length === 0) return entries

  for (const vault of vaults) {
    try {
      if (vault.isSmartCol || vault.isSmartDebt) continue

      const constants = vault.constantVariables
      const configs = vault.configs
      if (!constants || !configs) continue

      const rawCol = (
        constants.supplyToken?.token0 ??
        constants.supplyToken ??
        ''
      )
        .toString()
        .toLowerCase()
      const rawLoan = (
        constants.borrowToken?.token0 ??
        constants.borrowToken ??
        ''
      )
        .toString()
        .toLowerCase()
      if (!rawCol || !rawLoan) continue

      const collateral = normalizeUnderlying(rawCol)
      const loan = normalizeUnderlying(rawLoan)

      const colDec = tokenList?.[collateral]?.decimals ?? 18
      const loanDec = tokenList?.[loan]?.decimals ?? 18

      const rawPrice = configs.oraclePriceOperate?.toString() ?? '0'
      if (!rawPrice || rawPrice === '0') continue

      const priceDebtPerCol = priceColInDebt(rawPrice, colDec, loanDec)
      if (!Number.isFinite(priceDebtPerCol) || priceDebtPerCol === 0) continue

      const vaultId = Number(constants.vaultId ?? 0)
      const lenderKey = `${Lender.FLUID}_${chainId}_${vaultId}`

      // Loan-asset USD — same two-step lookup Morpho uses:
      // first the canonical oracle key (`assetGroup` or `${chainId}-${addr}`),
      // then the raw address as a fallback. Keeps the matching idiom
      // identical across all oracle fetchers so upstream writers and
      // downstream readers speak the same key shape.
      const loanOracleKey =
        tokenList?.[loan]?.assetGroup ?? `${chainId}-${loan}`
      const loanUSD = usdPrices[loanOracleKey] ?? usdPrices[loan]
      if (!loanUSD) continue

      // Loan side — base asset of the oracle, static 1.0 in its own unit.
      entries.push({
        asset: loan,
        price: 1,
        priceUSD: loanUSD,
        marketUid: createMarketUid(chainId, lenderKey, loan),
        targetLender: lenderKey,
        description: 'Fluid loan asset',
        staticBase: true,
        baseAsset: loan,
      })

      // Collateral side — price expressed in loan-asset terms.
      entries.push({
        asset: collateral,
        price: priceDebtPerCol,
        priceUSD: priceDebtPerCol * loanUSD,
        marketUid: createMarketUid(chainId, lenderKey, collateral),
        targetLender: lenderKey,
        baseAsset: loan,
      })
    } catch {
      // Skip malformed vaults — failures are per-entry.
    }
  }

  return entries
}

export function getFluidAbi() {
  return FluidVaultResolverAbi
}

/** Fluid Oracle Fetcher module */
export const fluidFetcher = {
  getCalls: getFluidCalls,
  parse: parseFluidResults,
  getAbi: getFluidAbi,
}
